import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Clock3,
  Copy,
  ExternalLink,
  Mail,
  MessageCircle,
  Package,
  Phone,
  Printer,
  ShoppingCart,
  Store,
  Truck,
  User,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OrderResponse, OrderStatus } from '@/services/ordersApi'
import {
  buildGoogleMapsAddressUrl,
  buildOrderReceiptUrl,
  buildOrderWhatsAppUrl,
  formatOrderMoney,
  formatOrderSummaryText,
  getOrderNextRecommendedAction,
  getOrderOriginBadge,
  getOrderProgressStepper,
  getOrderStatusBadge,
  type OrderStepperStep,
} from './ordersUx'
import OrderWhatsAppShareModal from './OrderWhatsAppShareModal'
import { getOrderPrimaryAction, resolveVerticalOrdersConfig, useVerticalUI } from '@/lib/verticalUI'

function when(value: string | null | undefined) {
  if (!value) return 'Sin fecha'
  const date = new Date(value)
  return Number.isNaN(date.getTime())
    ? 'Sin fecha'
    : date.toLocaleString('es-ES', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
}

function timeAgo(value: string | null | undefined) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const diffSec = Math.floor((Date.now() - date.getTime()) / 1000)
  if (diffSec < 60) return 'hace un momento'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `hace ${diffMin} min`
  const diffHours = Math.floor(diffMin / 60)
  if (diffHours < 24) return `hace ${diffHours} h`
  const diffDays = Math.floor(diffHours / 24)
  return `hace ${diffDays} d`
}

function renderBadgeToneClass(tone: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose') {
  if (tone === 'emerald') return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  if (tone === 'blue') return 'border-blue-200 bg-blue-50 text-blue-700'
  if (tone === 'violet') return 'border-violet-200 bg-violet-50 text-violet-700'
  if (tone === 'amber') return 'border-amber-200 bg-amber-50 text-amber-700'
  if (tone === 'indigo') return 'border-indigo-200 bg-indigo-50 text-indigo-700'
  if (tone === 'rose') return 'border-rose-200 bg-rose-50 text-rose-700'
  return 'border-slate-200 bg-slate-50 text-slate-600'
}

function getProgressStepClass(state: 'done' | 'current' | 'upcoming', tone: 'emerald' | 'blue' | 'violet' | 'slate' | 'amber' | 'indigo' | 'rose') {
  if (state === 'done') return 'border-emerald-300 bg-emerald-50 text-emerald-700'
  if (state === 'current') {
    if (tone === 'rose') return 'border-rose-400 bg-rose-100 text-rose-800 ring-2 ring-rose-200'
    if (tone === 'indigo') return 'border-indigo-400 bg-indigo-100 text-indigo-800 ring-2 ring-indigo-200'
    if (tone === 'blue') return 'border-blue-400 bg-blue-100 text-blue-800 ring-2 ring-blue-200'
    return 'border-amber-400 bg-amber-100 text-amber-800 ring-2 ring-amber-200'
  }
  return 'border-slate-200 bg-slate-50 text-slate-400'
}

function getFulfillmentIcon(method: string | null | undefined) {
  const m = (method || '').toLowerCase()
  if (m.includes('pickup') || m.includes('retiro') || m.includes('tienda')) {
    return <Store className="h-4 w-4 text-purple-600" />
  }
  if (m.includes('shipping') || m.includes('nacional') || m.includes('encomienda')) {
    return <Package className="h-4 w-4 text-blue-600" />
  }
  return <Truck className="h-4 w-4 text-[#075f55]" />
}

export interface OrderDetailViewProps {
  order: OrderResponse
  onEdit?: () => void
  onRepeat?: () => void
  onUpdateStatus?: (status: OrderStatus) => void
  onOpenStatusModal?: (status: Extract<OrderStatus, 'completed' | 'cancelled'>) => void
  isUpdatingStatus?: boolean
  onBack?: () => void
  /** El contenedor (drawer) puede mostrar la acción principal fija en su cabecera. */
  showNextAction?: boolean
}

export default function OrderDetailView({
  order,
  onEdit,
  onRepeat,
  onUpdateStatus,
  onOpenStatusModal,
  isUpdatingStatus = false,
  onBack,
  showNextAction = true,
}: OrderDetailViewProps) {
  const { profile } = useVerticalUI()
  const navigate = useNavigate()
  const [shareModalOpen, setShareModalOpen] = useState(false)

  const ordersConfig = resolveVerticalOrdersConfig(profile)
  const kanbanSteps: OrderStepperStep[] | undefined =
    ordersConfig.layout === 'kanban'
      ? ordersConfig.columns.map((column) => ({
          key: column.statuses[0] ?? column.id,
          label: column.label,
          matches: column.statuses,
          tone:
            column.id === 'new'
              ? 'amber'
              : column.id === 'kitchen'
                ? 'indigo'
                : 'emerald',
          icon: column.id === 'new' ? 'clock' : column.id === 'kitchen' ? 'arrow' : 'check',
        }))
      : undefined

  const statusBadge = getOrderStatusBadge(order.status)
  const progressSteps = getOrderProgressStepper(order.status, kanbanSteps)
  const originBadge = getOrderOriginBadge({ source: order.source, conversationId: order.conversationId })
  const nextAction = getOrderNextRecommendedAction(
    order.status,
    getOrderPrimaryAction(order.status, ordersConfig)
  )

  const customerPhone = order.customerPhone || order.contact?.phone || order.conversation?.waContact || ''
  const customerEmail = order.customerEmail || order.contact?.email || ''
  const mapsUrl = buildGoogleMapsAddressUrl(order.fulfillmentAddress)
  const directWhatsAppUrl = buildOrderWhatsAppUrl(customerPhone)
  const receiptUrl = buildOrderReceiptUrl(order.id)

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${label} copiado`)
    } catch {
      toast.error(`No se pudo copiar ${label}`)
    }
  }

  const handleCopySummary = () => {
    const summary = formatOrderSummaryText(order)
    copyToClipboard(summary, 'Resumen de orden')
  }

  const isEditable = order.status === 'draft' || order.status === 'pending_confirmation' || order.status === 'in_progress'

  return (
    <div className="flex flex-col space-y-4 p-4 lg:p-6">
      {/* Mobile Back / Sticky Nav Bar */}
      {onBack && (
        <div className="flex items-center justify-between border-b border-slate-200 pb-3 lg:hidden">
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a órdenes
          </button>
          <span className="text-xs font-mono text-slate-400">#{order.orderNumber}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-mono font-medium text-slate-400">#{order.orderNumber}</span>
              <span className="text-xs text-slate-400">·</span>
              <span className="text-xs text-slate-500">{when(order.createdAt)}</span>
              <span className="text-[11px] text-slate-400">({timeAgo(order.createdAt)})</span>
            </div>
            <h1 className="mt-1 text-xl font-bold text-slate-900 sm:text-2xl">
              {order.customerName || order.customerPhone || 'Cliente sin nombre'}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span
                className={[
                  'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold shadow-sm',
                  renderBadgeToneClass(statusBadge.tone),
                ].join(' ')}
              >
                {statusBadge.label}
              </span>
              <span className="text-[11px] text-slate-500">{originBadge.label}</span>
            </div>
          </div>

          {/* Quick Toolbar */}
          <div className="flex flex-wrap items-center gap-1.5 sm:justify-end">
            <button
              type="button"
              onClick={handleCopySummary}
              title="Copiar resumen para WhatsApp"
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Copy className="h-3.5 w-3.5 text-slate-500" />
              <span>Copiar resumen</span>
            </button>
            <button
              type="button"
              onClick={() => setShareModalOpen(true)}
              title="Notificar por WhatsApp"
              className="inline-flex items-center gap-1 rounded-lg border border-[#25D366]/30 bg-[#25D366]/10 px-2.5 py-1.5 text-xs font-semibold text-[#128C7E] shadow-sm transition hover:bg-[#25D366]/20"
            >
              <MessageCircle className="h-3.5 w-3.5 text-[#25D366]" />
              <span>Notificar</span>
            </button>
            {isEditable && onEdit && (
              <button
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1 rounded-lg border border-[#075f55]/20 bg-[#075f55]/5 px-2.5 py-1.5 text-xs font-semibold text-[#075f55] shadow-sm transition hover:bg-[#075f55]/10"
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                <span>Editar</span>
              </button>
            )}
            <button
              type="button"
              onClick={() => window.open(receiptUrl, '_blank', 'noopener,noreferrer')}
              title="Imprimir comprobante térmico o PDF"
              className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
            >
              <Printer className="h-3.5 w-3.5 text-slate-500" />
              <span>Recibo</span>
            </button>
          </div>
        </div>

        {/* Operational Progress Stepper */}
        <div className="mt-4 border-t border-slate-100 pt-3">
          <div className="flex flex-wrap items-center gap-2">
            {progressSteps.map((step, index) => (
              <div key={step.key} className="flex items-center gap-2">
                <span
                  className={[
                    'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium shadow-sm',
                    getProgressStepClass(step.state, step.tone),
                  ].join(' ')}
                >
                  {step.state === 'done' ? (
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  ) : step.state === 'current' ? (
                    <Clock3 className="h-3.5 w-3.5 animate-pulse" />
                  ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                  )}
                  {step.label}
                </span>
                {index < progressSteps.length - 1 && (
                  <span className="h-px w-3 bg-slate-200 sm:w-6" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Guided Next Best Action (CTA) */}
      {nextAction && showNextAction && (
        <div
          className={[
            'rounded-2xl border p-4 shadow-sm transition',
            nextAction.tone === 'blue'
              ? 'border-blue-200 bg-gradient-to-r from-blue-50/80 to-white'
              : nextAction.tone === 'indigo'
                ? 'border-indigo-200 bg-gradient-to-r from-indigo-50/80 to-white'
                : 'border-emerald-200 bg-gradient-to-r from-emerald-50/80 to-white',
          ].join(' ')}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-0.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Siguiente acción recomendada
              </span>
              <p className="text-sm font-semibold text-slate-900">{nextAction.description}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {onUpdateStatus && (
                <Button
                  type="button"
                  disabled={isUpdatingStatus}
                  onClick={() => onUpdateStatus(nextAction.nextStatus!)}
                  className={[
                    'inline-flex items-center gap-1.5 font-semibold text-white shadow',
                    nextAction.tone === 'blue'
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : nextAction.tone === 'indigo'
                        ? 'bg-indigo-600 hover:bg-indigo-700'
                        : 'bg-emerald-600 hover:bg-emerald-700',
                  ].join(' ')}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  {nextAction.label}
                </Button>
              )}
              {order.status !== 'cancelled' && onOpenStatusModal && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={isUpdatingStatus}
                  onClick={() => onOpenStatusModal('cancelled')}
                  className="inline-flex items-center gap-1 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                >
                  <Ban className="h-3.5 w-3.5" />
                  Cancelar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Two-Column Body */}
      <div className="grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        {/* Left Column: Items, Notes & Financial Summary */}
        <div className="space-y-4">
          {/* Items Card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-[#075f55]" />
                <h2 className="text-sm font-semibold text-slate-800">
                  Productos ({order.items.length})
                </h2>
              </div>
              <span className="text-xs text-slate-500">
                {order.itemCount || order.items.length} unidades totales
              </span>
            </div>

            <div className="mt-3 divide-y divide-slate-100">
              {order.items.length === 0 ? (
                <p className="py-4 text-center text-xs text-slate-400">No hay items registrados</p>
              ) : (
                order.items.map((item) => (
                  <div key={item.id} className="flex items-start justify-between gap-3 py-3">
                    <div className="flex items-start gap-3">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className="h-12 w-12 rounded-lg object-cover ring-1 ring-slate-100"
                        />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-400">
                          <Package className="h-6 w-6" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{item.name}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-slate-500">
                          {item.sku && (
                            <span className="font-mono text-[11px] text-slate-400">
                              SKU: {item.sku}
                            </span>
                          )}
                          {item.categoryName && (
                            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-600">
                              {item.categoryName}
                            </span>
                          )}
                          <span>
                            {formatOrderMoney(item.unitPriceCents, item.currency)} x {item.quantity}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">
                        {formatOrderMoney(item.lineTotalCents, item.currency)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Financial Summary */}
            <div className="mt-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
              <div className="space-y-1.5 text-xs text-slate-600">
                <div className="flex justify-between">
                  <span>Subtotal productos</span>
                  <span>{formatOrderMoney(order.subtotalCents || order.totalCents, order.currency)}</span>
                </div>
                {profile.operational?.hasFulfillment !== false && (
                  <div className="flex justify-between">
                    <span>Despacho / Entrega</span>
                    <span className="font-medium text-slate-700">Incluido</span>
                  </div>
                )}
                <div className="border-t border-slate-200 pt-2 flex items-baseline justify-between">
                  <span className="text-sm font-bold text-slate-900">Total a pagar</span>
                  <span className="text-xl font-extrabold text-[#075f55]">
                    {formatOrderMoney(order.totalCents, order.currency)}{' '}
                    <span className="text-xs font-normal text-slate-500">{order.currency}</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Card */}
          {order.notes && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 shadow-sm">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-800">
                Observaciones del pedido
              </p>
              <p className="mt-1.5 whitespace-pre-line text-sm text-amber-950">{order.notes}</p>
            </div>
          )}

          {/* Status History Timeline */}
          {order.history && order.history.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
                <Clock3 className="h-4 w-4 text-slate-600" />
                <h2 className="text-sm font-semibold text-slate-800">Historial y Auditoría</h2>
              </div>
              <div className="mt-4 space-y-0">
                {order.history.map((entry, index) => {
                  const isLast = index === order.history.length - 1
                  const tone =
                    entry.status === 'completed'
                      ? 'bg-emerald-500 ring-emerald-100'
                      : entry.status === 'cancelled'
                        ? 'bg-rose-500 ring-rose-100'
                        : entry.status === 'confirmed'
                          ? 'bg-blue-500 ring-blue-100'
                          : entry.status === 'in_progress'
                            ? 'bg-indigo-500 ring-indigo-100'
                            : 'bg-amber-500 ring-amber-100'

                  return (
                    <div key={`${entry.status}-${entry.at}`} className="relative flex gap-3 pb-4 last:pb-0">
                      <div className="flex flex-col items-center">
                        <span className={`mt-1.5 h-2.5 w-2.5 rounded-full ring-4 ${tone}`} />
                        {!isLast && <span className="mt-1 h-full w-px flex-1 bg-slate-200" />}
                      </div>
                      <div className="flex-1 rounded-xl bg-slate-50 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-1">
                          <p className="text-xs font-semibold text-slate-800">{entry.label}</p>
                          <span className="text-[11px] text-slate-500">{when(entry.at)}</span>
                        </div>
                        <p className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                          {entry.source}
                        </p>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Customer Card & Delivery (Fulfillment) Card */}
        <div className="space-y-4">
          {/* Customer Contact Card */}
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-[#075f55]" />
                <h2 className="text-sm font-semibold text-slate-800">Datos del Cliente</h2>
              </div>
              {order.conversationId && (
                <button
                  type="button"
                  onClick={() => navigate(`/app/conversations/${order.conversationId}`)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#075f55] hover:underline"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  Ver chat
                </button>
              )}
            </div>

            <div className="mt-3 space-y-3">
              <div>
                <p className="text-base font-bold text-slate-900">
                  {order.customerName || order.contact?.name || 'Cliente sin nombre registrado'}
                </p>
                {customerPhone ? (
                  <p className="font-mono text-xs text-slate-600">{customerPhone}</p>
                ) : (
                  <p className="text-xs text-slate-400">Sin teléfono registrado</p>
                )}
                {customerEmail && (
                  <p className="text-xs text-slate-500">{customerEmail}</p>
                )}
              </div>

              {/* Direct Communication Actions */}
              <div className="flex flex-wrap gap-2 pt-1">
                {directWhatsAppUrl && (
                  <a
                    href={directWhatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg border border-[#25D366]/40 bg-[#25D366]/10 px-3 py-1.5 text-xs font-semibold text-[#128C7E] transition hover:bg-[#25D366]/20"
                  >
                    <MessageCircle className="h-3.5 w-3.5" />
                    WhatsApp
                  </a>
                )}
                {customerPhone && (
                  <a
                    href={`tel:${customerPhone}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <Phone className="h-3.5 w-3.5 text-slate-500" />
                    Llamar
                  </a>
                )}
                {customerEmail && (
                  <a
                    href={`mailto:${customerEmail}`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <Mail className="h-3.5 w-3.5 text-slate-500" />
                    Correo
                  </a>
                )}
              </div>

              {/* Channel Instance Info */}
              {order.channelInstance && (
                <div className="rounded-xl border border-slate-100 bg-slate-50 p-2.5 text-xs text-slate-600">
                  <span className="font-semibold text-slate-800">Canal de atención: </span>
                  {order.channelInstance.instanceName} ({order.channelInstance.channelType})
                </div>
              )}
            </div>
          </div>

          {/* Fulfillment / Delivery Card: se omite en verticales sin despacho (salud, inmobiliaria, etc.) salvo que la orden incluya dirección */}
          {(profile.operational?.hasFulfillment !== false || Boolean(order.fulfillmentAddress)) && (
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                <div className="flex items-center gap-2">
                  {getFulfillmentIcon(order.fulfillmentMethod)}
                  <h2 className="text-sm font-semibold text-slate-800">Entrega y Despacho</h2>
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                  {order.fulfillmentMethod || 'Delivery'}
                </span>
              </div>

              <div className="mt-3 space-y-3">
                {order.fulfillmentAddress ? (
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                          Dirección de entrega
                        </p>
                        <p className="mt-1 text-sm font-medium text-slate-900">
                          {order.fulfillmentAddress}
                        </p>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => copyToClipboard(order.fulfillmentAddress!, 'Dirección')}
                        className="inline-flex items-center gap-1 text-xs text-[#075f55] hover:underline"
                      >
                        <Copy className="h-3 w-3" />
                        Copiar dirección
                      </button>
                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Ver en Google Maps
                        </a>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-xl bg-amber-50 p-3 text-xs text-amber-800">
                    <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
                    <span>No se ha registrado una dirección de entrega específica.</span>
                  </div>
                )}

                {order.fulfillmentReference && (
                  <div className="border-t border-slate-100 pt-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Punto de referencia / Sucursal
                    </p>
                    <p className="mt-0.5 text-sm text-slate-800">{order.fulfillmentReference}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Quick Repeat / Navigation Shortcuts */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
              Acciones Rápidas
            </p>
            <div className="mt-2.5 flex flex-col gap-2">
              {onRepeat && (
                <button
                  type="button"
                  onClick={onRepeat}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                >
                  🔁 Duplicar orden (Mismo cliente y productos)
                </button>
              )}
              {order.conversationId && (
                <button
                  type="button"
                  onClick={() => navigate(`/app/conversations/${order.conversationId}`)}
                  className="w-full rounded-xl border border-[#075f55]/20 bg-white px-3 py-2 text-left text-xs font-semibold text-[#075f55] shadow-sm transition hover:bg-[#075f55]/5"
                >
                  💬 Ir a la conversación del chat
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/app/products')}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-left text-xs font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
              >
                📦 Abrir catálogo de productos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Share Modal */}
      <OrderWhatsAppShareModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        order={order}
      />
    </div>
  )
}
